import React, { useState } from 'react';
import { Button } from './ui/button';
import { Phone, Mail, Copy, Check, Eye } from 'lucide-react';
import { toast } from 'sonner';

/**
 * RevealContact — anti-scrape contact reveal button.
 * Value is stored as character codes & assembled at click time, so bots scraping the HTML
 * won't find a readable phone/email string in the source.
 *
 * Props:
 *  - type: 'phone' | 'email'
 *  - label: short label shown on the button before reveal (e.g. "Téléphone", "Support")
 *  - value: the real phone/email
 *  - variant: 'card' (default) | 'inline' | 'dark' (for footer)
 *  - icon: optional override (lucide component)
 */
export const RevealContact = ({ type, label, value, variant = 'card', icon: IconOverride, testId }) => {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  // Anti-scrape: store as char codes, decode at runtime
  const encoded = React.useMemo(() => value.split('').map((c) => c.charCodeAt(0)), [value]);
  const decode = () => String.fromCharCode(...encoded);

  const handleReveal = (e) => {
    e.preventDefault();
    setRevealed(true);
  };

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(decode());
      setCopied(true);
      toast.success('Copié dans le presse-papier');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Impossible de copier');
    }
  };

  const Icon = IconOverride || (type === 'phone' ? Phone : Mail);
  const href = type === 'phone' ? `tel:${decode().replace(/[^+\d]/g, '')}` : `mailto:${decode()}`;

  // === Variant: dark (footer) ===
  if (variant === 'dark') {
    if (!revealed) {
      return (
        <button
          onClick={handleReveal}
          className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors group"
          data-testid={testId || `reveal-${type}-${label.toLowerCase().replace(/\s/g, '-')}`}
        >
          <Icon className="h-4 w-4" />
          <span className="underline decoration-dotted underline-offset-4 group-hover:decoration-solid">
            Afficher {label.toLowerCase()}
          </span>
        </button>
      );
    }
    return (
      <div className="flex items-center gap-2 text-sm" data-testid={`revealed-${type}-${label.toLowerCase().replace(/\s/g, '-')}`}>
        <Icon className="h-4 w-4 text-white shrink-0" />
        <a href={href} className="text-white hover:underline truncate">
          {decode()}
        </a>
        <button onClick={handleCopy} className="text-gray-400 hover:text-white p-1 -m-1" title="Copier">
          {copied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
    );
  }

  // === Variant: inline (small text) ===
  if (variant === 'inline') {
    if (!revealed) {
      return (
        <button
          onClick={handleReveal}
          className="inline-flex items-center gap-1.5 text-sm text-black underline decoration-dotted underline-offset-4 hover:decoration-solid"
          data-testid={testId || `reveal-${type}`}
        >
          <Eye className="h-3.5 w-3.5" />
          Afficher {label.toLowerCase()}
        </button>
      );
    }
    return (
      <span className="inline-flex items-center gap-2">
        <a href={href} className="text-black font-mono text-sm hover:underline">
          {decode()}
        </a>
        <button onClick={handleCopy} className="text-gray-500 hover:text-black" title="Copier">
          {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </span>
    );
  }

  // === Variant: card (default - for Contact page) ===
  if (!revealed) {
    return (
      <Button
        onClick={handleReveal}
        variant="outline"
        className="w-full border-gray-300 text-black hover:bg-gray-50 justify-start gap-3"
        data-testid={testId || `reveal-${type}-${label.toLowerCase().replace(/\s/g, '-')}`}
      >
        <Icon className="h-5 w-5" />
        <span className="flex-1 text-left">
          <span className="block text-xs text-gray-500 font-normal">{label}</span>
          <span className="block font-semibold">Cliquer pour afficher</span>
        </span>
        <Eye className="h-4 w-4 text-gray-400" />
      </Button>
    );
  }

  return (
    <div
      className="w-full border border-black rounded-md p-3 bg-gray-50 flex items-center gap-3"
      data-testid={`revealed-${type}-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <Icon className="h-5 w-5 text-black shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <a href={href} className="text-black font-semibold hover:underline break-all">
          {decode()}
        </a>
      </div>
      <button
        onClick={handleCopy}
        className="text-gray-600 hover:text-black p-2 -m-2 shrink-0"
        title="Copier"
        data-testid={`copy-${type}-${label.toLowerCase().replace(/\s/g, '-')}`}
      >
        {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
};

export default RevealContact;
